import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { adminVendorApps } from "@/lib/vendorApi";
import {
  adminListPincodes, adminCreatePincode, adminUpdatePincode,
  adminTogglePincode, adminDeletePincode, adminBulkImportPincodes,
  adminListPinImports,
  adminListAds, adminApproveAd, adminRejectAd, adminPauseAd,
} from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Check, MapPin, Plus, Trash2, Edit3, Eye, EyeOff, X, Upload, Download, Search, History, ChevronLeft, ChevronRight, Megaphone, CheckCircle2, XCircle, PauseCircle, Paperclip, BarChart3 } from "lucide-react";
import AdminActivityReviews from "@/components/AdminActivityReviews";
import AdminAdSettings from "@/components/AdminAdSettings";
import AdminLibraryMigration from "@/components/AdminLibraryMigration";
import AdminBackupStatus from "@/components/AdminBackupStatus";
import AdminAccountReview from "@/components/AdminAccountReview";
import AdminSitePause from "@/components/AdminSitePause";
import AdminHelpContact from "@/components/AdminHelpContact";
import AdminAccountSettings from "@/components/AdminAccountSettings";
import AdminStats from "@/components/AdminStats";
import AdminExpiredJobs from "@/components/AdminExpiredJobs";
import AdminShopAlerts from "@/components/AdminShopAlerts";
import { fetchAdminShopAlerts } from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { AdStatusBadge } from "@/components/vendor/AdvertiseDialog";
import { AdReport } from "@/components/vendor/AdInsights";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function AdminPanel() {
  const { user, isAdmin, authChecking, formatApiError } = useApp();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pins, setPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(true);
  const [pinForm, setPinForm] = useState(null); // null | {pincode?, area, city, state, lat, lng}
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "ads" ? "ads" : "vendors"));
  const [rejectAd, setRejectAd] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pinSearch, setPinSearch] = useState("");
  const [imports, setImports] = useState({ items: [], total: 0, limit: 20, skip: 0 });
  const [importsLoading, setImportsLoading] = useState(false);
  const bulkInputRef = useRef(null);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adBusy, setAdBusy] = useState(null);
  const [reportAd, setReportAd] = useState(null);
  const [shopAlertsUnseen, setShopAlertsUnseen] = useState(0);
  useEffect(() => {
    if (isAdmin) fetchAdminShopAlerts().then((r) => setShopAlertsUnseen(r.unseen)).catch(() => {});
  }, [isAdmin]);
  const loadAds = async () => {
    setAdsLoading(true);
    try { setAds(await adminListAds()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setAdsLoading(false); }
  };

  const loadApps = async () => {
    setLoading(true);
    try {
      const list = await adminVendorApps();
      setApps(list);
    } finally { setLoading(false); }
  };

  const loadPins = async (search = pinSearch) => {
    setPinsLoading(true);
    try { setPins(await adminListPincodes(search.trim())); }
    finally { setPinsLoading(false); }
  };

  const loadImports = async (skip = 0) => {
    setImportsLoading(true);
    try {
      const res = await adminListPinImports(20, skip);
      setImports(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportsLoading(false); }
  };

  useEffect(() => {
    if (authChecking) return;
    if (!user || !isAdmin) { toast.error("Admin only"); navigate("/", { replace: true }); return; }
    loadApps();
    loadPins("");
  }, [user, isAdmin, authChecking]);

  // Debounced PIN search
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => { loadPins(pinSearch); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [pinSearch]);

  useEffect(() => {
    if (tab === "imports" && isAdmin) loadImports(0);
    if (tab === "ads" && isAdmin) loadAds();
    // eslint-disable-next-line
  }, [tab, isAdmin]);

  const actOnAd = async (adId, action) => {
    setAdBusy(adId + action);
    try {
      if (action === "approve") await adminApproveAd(adId);
      else if (action === "reject") { await adminRejectAd(adId, rejectNote.trim()); setRejectAd(null); setRejectNote(""); }
      else if (action === "pause") await adminPauseAd(adId);
      toast.success(`Advertisement ${{ approve: "approved", reject: "rejected", pause: "paused" }[action]}`);
      loadAds();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdBusy(null); }
  };

  const openAdd = () => setPinForm({ pincode: "", area: "", city: "", state: "", lat: "", lng: "", active: true });
  const openEdit = (p) => setPinForm({ ...p });

  const savePin = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        pincode: pinForm.pincode.trim(),
        area: pinForm.area.trim(),
        city: pinForm.city.trim(),
        state: pinForm.state.trim(),
        lat: parseFloat(pinForm.lat),
        lng: parseFloat(pinForm.lng),
        active: !!pinForm.active,
      };
      if (pinForm._isEdit) {
        await adminUpdatePincode(pinForm.pincode, payload);
        toast.success("PIN updated");
      } else {
        await adminCreatePincode(payload);
        toast.success("PIN added");
      }
      setPinForm(null);
      loadPins();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const togglePin = async (p) => {
    try {
      await adminTogglePincode(p.pincode, !p.active);
      toast.success(p.active ? "Deactivated" : "Activated");
      loadPins();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePin = async (p) => {
    if (!confirm(`Delete PIN ${p.pincode}?`)) return;
    try {
      await adminDeletePincode(p.pincode);
      toast.success("Deleted");
      loadPins();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkFile = async (f) => {
    if (!f) return;
    setBulkBusy(true);
    try {
      const res = await adminBulkImportPincodes(f);
      toast.success(`Imported — ${res.created} new, ${res.updated} updated` + (res.errors?.length ? `, ${res.errors.length} errors` : ""));
      if (res.errors?.length) {
        res.errors.slice(0, 3).forEach((e) => toast.error(`Row ${e.row}: ${e.detail}`));
      }
      loadPins();
      // Refresh audit history silently if that tab is cached
      if (tab === "imports") loadImports(0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  };

  const downloadSample = () => {
    const csv = "pincode,area,city,state,lat,lng,active\n110001,Connaught Place,New Delhi,Delhi,28.6315,77.2167,true\n110002,Daryaganj,New Delhi,Delhi,28.6456,77.2419,true\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "verb-pincodes-sample.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10" data-testid="admin-panel">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-4"><ArrowLeft className="w-4 h-4 rtl-flip" /> Back to home</Link>
      <div className="flex items-center gap-2 mb-6">
        <ShieldCheck className="w-6 h-6 text-emerald-700" />
        <h1 className="font-display text-3xl font-bold">Admin Panel</h1>
      </div>
      <AdminBackupStatus />
      <AdminSitePause />
      <AdminHelpContact />
      <AdminAccountSettings />

      <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar border-b border-slate-100 mb-5" data-testid="admin-tabs">
        <TabBtn active={tab === "vendors"} onClick={() => setTab("vendors")} testid="admin-tab-vendors">
          Vendor Applications ({apps.length})
        </TabBtn>
        <TabBtn active={tab === "pins"} onClick={() => setTab("pins")} testid="admin-tab-pins">
          Serviceable PINs
        </TabBtn>
        <TabBtn active={tab === "imports"} onClick={() => setTab("imports")} testid="admin-tab-imports">
          Import History
        </TabBtn>
        <TabBtn active={tab === "ads"} onClick={() => setTab("ads")} testid="admin-tab-ads">
          Ads
        </TabBtn>
        <TabBtn active={tab === "account-review"} onClick={() => setTab("account-review")} testid="admin-tab-account-review">
          Account Review
        </TabBtn>
        <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")} testid="admin-tab-reviews">
          Listing Reviews
        </TabBtn>
        <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} testid="admin-tab-stats">
          Stats
        </TabBtn>
        <TabBtn active={tab === "expired-jobs"} onClick={() => setTab("expired-jobs")} testid="admin-tab-expired-jobs">
          Expired Jobs
        </TabBtn>
        <TabBtn active={tab === "shop-alerts"} onClick={() => setTab("shop-alerts")} testid="admin-tab-shop-alerts">
          New Shops{shopAlertsUnseen > 0 && <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1" data-testid="admin-shop-alerts-badge">{shopAlertsUnseen}</span>}
        </TabBtn>
      </div>
      {tab === "stats" && <AdminStats />}
      {tab === "expired-jobs" && <AdminExpiredJobs />}
      {tab === "shop-alerts" && <AdminShopAlerts onUnseenChange={setShopAlertsUnseen} />}

      {tab === "vendors" && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6" data-testid="vendor-apps-section">
          <h2 className="font-display text-xl font-bold mb-1">Vendor Applications ({apps.length})</h2>
          <p className="text-xs text-slate-500 mb-4" data-testid="vendor-apps-note">View only. Vendors are activated automatically after verified signup — no approval needed.</p>
          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
          ) : apps.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No vendor applications yet.</div>
          ) : (
            <div className="space-y-3">
              {apps.map((a) => (
                <div key={a.id} className="border border-slate-100 rounded-xl p-4 min-w-0" data-testid={`vendor-app-${a.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold break-words">{a.shop_name}</div>
                      <div className="text-sm text-slate-500 break-all">{a.name} · {a.email}</div>
                      <div className="text-xs text-slate-400 mt-1">Phone: {a.phone} · City: {a.city || "-"}{a.created_at ? ` · ${new Date(a.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}` : ""}</div>
                      {a.message && <div className="text-sm text-slate-600 mt-2 italic break-words">"{a.message}"</div>}
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${a.account_status === "active" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`vendor-app-status-${a.id}`}>
                      {a.account_status === "active" ? <><Check className="w-3.5 h-3.5" /> Active</> : <span className="capitalize">{a.account_status || "unknown"}</span>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "pins" && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6" data-testid="pin-registry-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-700" /> Serviceable PIN codes</h2>
              <p className="text-xs text-slate-500 mt-0.5">Add new PIN codes to expand VerB coverage. No code deploy required.</p>
            </div>
            <button onClick={openAdd} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold" data-testid="add-pin-btn">
              <Plus className="w-4 h-4" /> Add PIN
            </button>
          </div>

          <div className="mb-4 relative" data-testid="pin-search-wrap">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={pinSearch}
              onChange={(e) => setPinSearch(e.target.value)}
              type="text"
              inputMode="search"
              placeholder="Search PIN, area, city or state..."
              className="w-full border border-slate-200 rounded-xl pl-9 pr-9 py-2.5 text-sm outline-none focus:border-emerald-500"
              data-testid="pin-search-input"
            />
            {pinSearch && (
              <button type="button" onClick={() => setPinSearch("")} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100" data-testid="pin-search-clear">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>

          <div className="mb-5 border border-emerald-100 bg-emerald-50/60 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="bulk-import-section">
            <input ref={bulkInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleBulkFile(e.target.files?.[0])} data-testid="bulk-csv-input" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900">Bulk import from CSV</div>
              <div className="text-xs text-emerald-800/80">Columns: pincode, area, city, state, lat, lng, active (optional)</div>
            </div>
            <button onClick={downloadSample} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-200 text-emerald-800 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-50" data-testid="download-sample-btn">
              <Download className="w-3.5 h-3.5" /> Sample
            </button>
            <button onClick={() => bulkInputRef.current?.click()} disabled={bulkBusy} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold" data-testid="bulk-upload-btn">
              <Upload className="w-3.5 h-3.5" /> {bulkBusy ? "Importing..." : "Upload CSV"}
            </button>
          </div>

          {pinsLoading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
          ) : pins.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="pins-empty">
              {pinSearch.trim() ? `No PINs match "${pinSearch}".` : `No admin-added PINs yet. Click "Add PIN" to register one.`}
            </div>
          ) : (
            <>
              <div className="text-[11px] uppercase font-semibold text-slate-400 mb-2">
                {pins.length} result{pins.length === 1 ? "" : "s"}
                {pinSearch.trim() ? ` for “${pinSearch.trim()}”` : ""}
              </div>
              <div className="space-y-2">
                {pins.map((p) => (
                  <div key={p.pincode} className={`flex items-center gap-3 border rounded-xl p-3 ${p.active ? "border-slate-100" : "border-slate-200 bg-slate-50"}`} data-testid={`pin-row-${p.pincode}`}>
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 inline-flex items-center justify-center flex-shrink-0 font-bold text-xs">{p.pincode}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-slate-900 truncate">{p.area}</div>
                        {!p.active && <span className="text-[10px] uppercase font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">Inactive</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{p.city}, {p.state} · {p.lat?.toFixed(4)}, {p.lng?.toFixed(4)}</div>
                    </div>
                    <button onClick={() => openEdit({ ...p, _isEdit: true })} className="p-2 text-slate-500 hover:text-emerald-700" aria-label="Edit" data-testid={`edit-pin-${p.pincode}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => togglePin(p)} className="p-2 text-slate-500 hover:text-amber-700" aria-label="Toggle" data-testid={`toggle-pin-${p.pincode}`}>
                      {p.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => deletePin(p)} className="p-2 text-slate-400 hover:text-red-600" aria-label="Delete" data-testid={`delete-pin-${p.pincode}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "imports" && <div className="mb-4"><AdminLibraryMigration /></div>}
      {tab === "imports" && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6" data-testid="imports-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-emerald-700" /> CSV Import History</h2>
              <p className="text-xs text-slate-500 mt-0.5">Audit trail of every PIN CSV bulk import. Records are immutable.</p>
            </div>
            <button onClick={() => loadImports(imports.skip)} className="text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full px-3 py-1.5" data-testid="imports-refresh">
              Refresh
            </button>
          </div>

          {importsLoading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
          ) : imports.items.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="imports-empty">
              No CSV imports yet. Head to the Serviceable PINs tab and upload one.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-5 sm:mx-0" data-testid="imports-table-wrap">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                      <th className="px-3 py-2 font-semibold">When</th>
                      <th className="px-3 py-2 font-semibold">Admin</th>
                      <th className="px-3 py-2 font-semibold">File</th>
                      <th className="px-3 py-2 font-semibold text-right">Total</th>
                      <th className="px-3 py-2 font-semibold text-right">Added</th>
                      <th className="px-3 py-2 font-semibold text-right">Updated</th>
                      <th className="px-3 py-2 font-semibold text-right">Failed</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imports.items.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100 align-top" data-testid={`import-row-${a.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">{new Date(a.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 text-slate-700">
                          <div className="font-semibold truncate max-w-[160px]">{a.admin_name || "Admin"}</div>
                          <div className="text-xs text-slate-400 truncate max-w-[160px]">{a.admin_email || ""}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          <div className="truncate max-w-[180px]" title={a.filename}>{a.filename}</div>
                          {a.error_summary && <div className="text-[11px] text-red-600 truncate max-w-[220px]" title={a.error_summary}>{a.error_summary}</div>}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">{a.total ?? 0}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{a.added ?? 0}</td>
                        <td className="px-3 py-2 text-right text-sky-700 font-semibold">{a.updated ?? 0}</td>
                        <td className="px-3 py-2 text-right text-red-700 font-semibold">{a.failed ?? 0}</td>
                        <td className="px-3 py-2"><StatusBadge status={a.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 text-xs text-slate-500" data-testid="imports-pagination">
                <div>Showing {imports.skip + 1}–{Math.min(imports.skip + imports.items.length, imports.total)} of {imports.total}</div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => loadImports(Math.max(0, imports.skip - imports.limit))}
                    disabled={imports.skip === 0}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40"
                    data-testid="imports-prev"
                  ><ChevronLeft className="w-3 h-3" /> Prev</button>
                  <button
                    onClick={() => loadImports(imports.skip + imports.limit)}
                    disabled={imports.skip + imports.items.length >= imports.total}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40"
                    data-testid="imports-next"
                  >Next <ChevronRight className="w-3 h-3" /></button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "reviews" && <AdminActivityReviews />}
      {tab === "account-review" && <AdminAccountReview />}

      {tab === "ads" && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6" data-testid="ads-section">
          <AdminAdSettings />
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><Megaphone className="w-5 h-5 text-emerald-700" /> Advertisement Review Desk</h2>
              <p className="text-xs text-slate-500 mt-0.5">Approve paid ads to make them visible to shoppers in the target PIN.</p>
            </div>
            <button onClick={loadAds} className="text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full px-3 py-1.5" data-testid="ads-refresh">
              Refresh
            </button>
          </div>

          {adsLoading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
          ) : ads.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="ads-empty">
              No advertisements yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="ads-list">
              {ads.map((a) => (
                <div key={a.id} className="border border-slate-100 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row gap-3" data-testid={`ad-row-${a.id}`}>
                  <img src={a.preview?.image || ""} alt="" className="w-full sm:w-20 h-24 sm:h-20 rounded-lg object-cover bg-slate-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{a.preview?.name || a.target_id} <span className="text-slate-400 font-normal">· {a.target_type}</span></div>
                        <div className="text-[11px] text-slate-500 truncate">By {a.vendor_name || a.vendor_email || "vendor"}{a.vendor_name && a.vendor_email ? ` · ${a.vendor_email}` : ""}</div>
                        <div className="text-[10px] text-slate-400 truncate" data-testid={`admin-ad-id-${a.id}`}>ID {a.id}{a.created_at ? ` · Submitted ${new Date(a.created_at).toLocaleString()}` : ""}</div>
                      </div>
                      <AdStatusBadge status={a.status} />
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                      <span>PIN <b className="text-slate-700 break-all" data-testid={`admin-ad-pins-${a.id}`}>{(a.pincodes || [a.pincode]).join(", ")}</b></span>
                      <span>Duration <b className="text-slate-700">{a.duration_days}d</b></span>
                      <span>Price <b className="text-slate-700">₹{a.price}</b></span>
                      <span>Paid <b className={a.paid ? "text-emerald-700" : "text-rose-700"}>{a.paid ? "Yes" : "No"}</b></span>
                      {a.ends_at && <span>Ends <b className="text-slate-700">{new Date(a.ends_at).toLocaleDateString()}</b></span>}
                    </div>
                    {(a.title || a.message) && <div className="mt-1 text-xs text-slate-700"><b>{a.title}</b>{a.message ? ` — ${a.message}` : ""}</div>}
                    {(a.attachments || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`ad-attachments-${a.id}`}>
                        {a.attachments.map((f) => (
                          <a key={f.file_id} href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 max-w-[14rem] rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700 hover:border-emerald-400">
                            <Paperclip className="w-3 h-3 flex-shrink-0" /><span className="truncate">{f.filename}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(a.status === "pending_approval" || a.status === "paused") && (
                        <button onClick={() => actOnAd(a.id, "approve")} disabled={adBusy || !a.paid} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-semibold" data-testid={`ad-approve-${a.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                        </button>
                      )}
                      {a.status === "active" && (
                        <button onClick={() => actOnAd(a.id, "pause")} disabled={adBusy} className="inline-flex items-center gap-1 rounded-full bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 text-xs font-semibold" data-testid={`ad-pause-${a.id}`}>
                          <PauseCircle className="w-3.5 h-3.5" /> Pause
                        </button>
                      )}
                      {(a.status === "pending_payment" || a.status === "pending_approval") && (
                        <button onClick={() => { setRejectNote(""); setRejectAd(a); }} disabled={adBusy} className="inline-flex items-center gap-1 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 py-1.5 text-xs font-semibold" data-testid={`ad-reject-${a.id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      )}
                      <button onClick={() => setReportAd(a)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white hover:border-emerald-400 text-slate-700 px-3 py-1.5 text-xs font-semibold" data-testid={`ad-view-report-${a.id}`}>
                        <BarChart3 className="w-3.5 h-3.5" /> View Report
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!rejectAd} onOpenChange={(o) => !o && setRejectAd(null)}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] p-4 sm:p-6" data-testid="admin-ad-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Reject ad · {rejectAd?.title || rejectAd?.preview?.name || rejectAd?.target_id}</DialogTitle>
            <DialogDescription className="text-xs">The reason is shown to the vendor next to the Rejected status.</DialogDescription>
          </DialogHeader>
          <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} maxLength={500} rows={3} placeholder="Reason (optional), e.g. image is unclear" className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-rose-400" data-testid="admin-ad-reject-reason" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setRejectAd(null)} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600" data-testid="admin-ad-reject-cancel">Cancel</button>
            <button onClick={() => actOnAd(rejectAd.id, "reject")} disabled={!!adBusy} className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-4 py-2 text-xs font-semibold" data-testid="admin-ad-reject-confirm">
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reportAd} onOpenChange={(o) => !o && setReportAd(null)}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto p-4 sm:p-6" data-testid="admin-ad-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Ad report · {reportAd?.preview?.name || reportAd?.target_id}</DialogTitle>
            <DialogDescription className="text-xs">Campaign {reportAd?.id} · by {reportAd?.vendor_name || "vendor"}</DialogDescription>
          </DialogHeader>
          {reportAd && <AdReport adId={reportAd.id} />}
        </DialogContent>
      </Dialog>

      {pinForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-slate-900/60 overflow-y-auto" onClick={() => setPinForm(null)}>
          <form onSubmit={savePin} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto p-5" data-testid="pin-form-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{pinForm._isEdit ? `Edit PIN ${pinForm.pincode}` : "Add PIN"}</h3>
              <button type="button" onClick={() => setPinForm(null)} aria-label="Close" className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <PinField label="PIN code (6 digits)*" value={pinForm.pincode} onChange={(v) => setPinForm({ ...pinForm, pincode: v.replace(/\D/g, "").slice(0, 6) })} disabled={pinForm._isEdit} testid="pin-input-pincode" />
              <PinField label="Area / Locality*" value={pinForm.area} onChange={(v) => setPinForm({ ...pinForm, area: v })} testid="pin-input-area" />
              <div className="grid grid-cols-2 gap-3">
                <PinField label="City*" value={pinForm.city} onChange={(v) => setPinForm({ ...pinForm, city: v })} testid="pin-input-city" />
                <PinField label="State*" value={pinForm.state} onChange={(v) => setPinForm({ ...pinForm, state: v })} testid="pin-input-state" />
                <PinField label="Latitude*" value={pinForm.lat} onChange={(v) => setPinForm({ ...pinForm, lat: v })} type="number" step="0.000001" testid="pin-input-lat" />
                <PinField label="Longitude*" value={pinForm.lng} onChange={(v) => setPinForm({ ...pinForm, lng: v })} type="number" step="0.000001" testid="pin-input-lng" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!pinForm.active} onChange={(e) => setPinForm({ ...pinForm, active: e.target.checked })} data-testid="pin-input-active" />
                Active (visible as serviceable PIN)
              </label>
              <button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl py-3 text-sm font-semibold" data-testid="pin-save-btn">
                {pinForm._isEdit ? "Save changes" : "Add PIN"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${active ? "border-emerald-700 text-emerald-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
      {children}
    </button>
  );
}

function PinField({ label, value, onChange, type = "text", step, testid, disabled }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} type={type} step={step} disabled={disabled} required className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500 disabled:bg-slate-50 disabled:text-slate-400" data-testid={testid} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    success: { label: "Success", cls: "bg-emerald-100 text-emerald-800" },
    partial: { label: "Partial", cls: "bg-amber-100 text-amber-800" },
    failed: { label: "Failed", cls: "bg-red-100 text-red-800" },
    started: { label: "In progress", cls: "bg-slate-100 text-slate-700" },
  };
  const m = map[status] || { label: status || "Unknown", cls: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
}
